import { Component, componentMixin } from '@resn/gozer-core/component';
import DomComponentMixin from '@resn/gozer-core-mixins/dom-component';
import { autobind } from 'core-decorators';
import { html, classMap } from '@resn/gozer-core';
import './index.scss';
import svg from 'component/svg';
import splitText from 'utils/gsap/split-text';
import gsap from 'gsap/gsap-core';
import store from 'core/store';

const template = ({ context, options }) => {
    const classes = { 'animatedLink--primary': options.hasStylePrimary };
    return html`
        <div
            class="animatedLink ${classMap(classes)}"
            @mouseover=${context.onMouseOver}
            @mouseout=${context.onMouseOut}
            @click=${context.onClick}
        >
            <span class="animatedLink__arrow" data-ui="arrow">${svg('arrowhead-right', 'arrow__icon')}</span>
            <span>${options.label}</span>
            ${options.hasExtraIcon
                ? html`<span class="animatedLink__extra">${svg(options.hasExtraIcon, 'extra__icon')}</span>`
                : html``}
        </div>
    `;
};

@componentMixin(DomComponentMixin)
class AnimatedLink extends Component {
    props = {
        invalidate: false,
        open: true,
        arrow: true,

        selectedOver: false,
        selected: (props) => props.selectedOver,

        hasOpenAnimation: true,
        hasBigVersion: false,
        hasExtraIcon: null,
    };

    options = {
        template,
    };

    async onSetup() {
        this.split = new splitText(this.el, { type: 'chars', charsClass: 'c' });

        this.split.chars.forEach((char) => {
            char.letter = char.innerText;
            char.innerHTML = null;
        });

        this.split.charsDefault = this.split.chars.map((char) => {
            const s = document.createElement('span');
            s.innerText = char.letter;
            char.appendChild(s);
            return s;
        });
        this.split.charsActive = this.split.chars.map((char) => {
            const s = document.createElement('span');
            s.classList = ['on'];
            s.innerText = char.letter;
            char.appendChild(s);
            return s;
        });
    }

    onOpen() {
        this.props.invalidate = true;

        if (this.tl) this.tl.kill();
        this.tl = gsap
            .timeline({ onComplete: () => (this.props.invalidate = false) })
            .set(this.el, { x: this.props.selected ? (store().state.mobile ? 25 : 20) : 0 }, 0)
            .set(this.split.charsActive, { x: 0, opacity: this.props.selected ? 1 : 0 }, 0)
            .set(this.split.charsDefault, { x: 0, opacity: this.props.selected ? 0 : 1 }, 0)
            .set(this.ui.arrow, { opacity: this.props.selected ? 1 : 0 }, 0);
    }

    offOpen() {
        const { hasOpenAnimation } = this.options;

        if (hasOpenAnimation) {
            if (this.tl) this.tl.kill();
            this.tl = gsap
                .timeline({})
                .to(
                    [this.split.charsDefault, this.split.charsActive],
                    {
                        duration: 0.3,
                        x: '104%',
                        opacity: 0,
                        ease: 'power3.in',
                    },
                    0,
                )
                .to(this.ui.arrow, { opacity: 0, duration: 0.5, ease: 'power3.out' }, 0);
        }
    }

    onSelected() {
        if (this.props.invalidate || !this.props.open) return;
        if (this.tl) this.tl.kill();
        this.tl = gsap
            .timeline({})
            .to(
                this.el,
                {
                    duration: 0.48,
                    x: 20,
                    ease: 'power3.out',
                },
                0,
            )
            .to(
                this.split.charsDefault,
                {
                    duration: 0.42,
                    opacity: 0,
                    ease: 'power2.out',
                },
                0,
            )
            .to(
                this.split.charsActive,
                {
                    duration: 0.5,
                    stagger: 0.02,
                    opacity: 1,
                    ease: 'power3.out',
                },
                0,
            )
            .to(this.ui.arrow, { opacity: 1, duration: 0.5, ease: 'power3.out' }, 0);
    }

    offSelected() {
        if (this.props.invalidate || !this.props.open) return;
        if (this.tl) this.tl.kill();
        this.tl = gsap
            .timeline({})
            .to(
                this.el,
                {
                    duration: 0.5,
                    x: 0,
                    ease: 'power2.out',
                },
                0,
            )
            .to(
                this.split.charsDefault,
                {
                    duration: 0.4,
                    opacity: 1,
                    ease: 'power3.out',
                },
                0.1,
            )
            .to(
                this.split.charsActive,
                {
                    duration: 0.5,
                    opacity: 0,
                    ease: 'power3.out',
                },
                0,
            )
            .to(this.ui.arrow, { opacity: 0, duration: 0.5, ease: 'power3.out' }, 0);
    }

    @autobind
    onMouseOver() {
        this.props.selectedOver = true;
    }
    @autobind
    onMouseOut() {
        this.props.selectedOver = false;
    }
    @autobind
    onClick(e) {
        if (this.options.cbClick) this.options.cbClick(e);
    }
}

export default AnimatedLink;
